import { useCallback, useEffect, useState } from 'react'
import { marketSegmentationApi } from '@/lib/api/client'
import type {
  CreatePersonaInput,
  CreateSegmentInput,
  CoverageLevel,
  MarketPersona,
  MarketSegmentationCanvas,
  MarketSegmentationCanvasBundle,
  MarketSegmentWithScore,
  SegmentPersonaLink,
  UpdatePersonaInput,
  UpdateSegmentInput,
  Weights,
} from '@ispot/shared'
import {
  applyUserEditScore,
  applyUserEditSizing,
  EMPTY_SCORE_FIELD,
  EMPTY_SIZING_FIELD,
  resetScoreToAI,
  resetSizingToAI,
  SCORING_FIELD_KEYS,
  SIZING_FIELD_KEYS,
  type ScoringFieldKey,
  type SizingFieldKey,
} from '@ispot/shared'

interface MarketSegmentationState {
  canvas: MarketSegmentationCanvas | null
  segments: MarketSegmentWithScore[]
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  isLoading: boolean
  error: string | null
}

const EMPTY_STATE: MarketSegmentationState = {
  canvas: null,
  segments: [],
  personas: [],
  coverage: [],
  isLoading: false,
  error: null,
}

export function useMarketSegmentation(accountId: string, projectId: string) {
  const [state, setState] = useState<MarketSegmentationState>(EMPTY_STATE)

  // ==========================================================================
  // LOAD
  // ==========================================================================

  const load = useCallback(async () => {
    if (!accountId || !projectId) return
    setState((s) => ({ ...s, isLoading: true, error: null }))
    try {
      const bundle = (await marketSegmentationApi.getCanvas(
        accountId,
        projectId,
      )) as MarketSegmentationCanvasBundle
      setState({
        canvas: bundle.canvas,
        segments: bundle.segments,
        personas: bundle.personas,
        coverage: bundle.coverage,
        isLoading: false,
        error: null,
      })
    } catch (err) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load canvas',
      }))
    }
  }, [accountId, projectId])

  useEffect(() => {
    void load()
  }, [load])

  // ==========================================================================
  // CANVAS MUTATIONS
  // ==========================================================================

  const updateWeights = useCallback(
    async (weights: Weights) => {
      // Optimistic
      setState((s) => (s.canvas ? { ...s, canvas: { ...s.canvas, weights } } : s))
      try {
        const { canvas } = await marketSegmentationApi.updateWeights(
          accountId,
          projectId,
          weights,
        )
        setState((s) => ({ ...s, canvas }))
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  const setBeachhead = useCallback(
    async (segmentId: string | null) => {
      const prev = state.canvas
      setState((s) =>
        s.canvas ? { ...s, canvas: { ...s.canvas, beachheadSegmentId: segmentId } } : s,
      )
      try {
        const { canvas } = await marketSegmentationApi.setBeachhead(accountId, projectId, segmentId)
        setState((s) => ({ ...s, canvas }))
      } catch (err) {
        if (prev) setState((s) => ({ ...s, canvas: prev }))
        throw err
      }
    },
    [accountId, projectId, state.canvas],
  )

  // ==========================================================================
  // SEGMENT MUTATIONS
  // ==========================================================================

  const createSegment = useCallback(
    async (input: CreateSegmentInput) => {
      const { segment } = await marketSegmentationApi.createSegment(accountId, projectId, input)
      setState((s) => ({
        ...s,
        segments: [...s.segments, { ...segment, weightedScore: 0 }],
      }))
      return segment as MarketSegmentWithScore
    },
    [accountId, projectId],
  )

  const updateSegment = useCallback(
    async (id: string, updates: UpdateSegmentInput) => {
      setState((s) => ({
        ...s,
        segments: s.segments.map((seg) => (seg.id === id ? { ...seg, ...updates } : seg)),
      }))
      try {
        const { segment } = await marketSegmentationApi.updateSegment(
          accountId,
          projectId,
          id,
          updates as Record<string, unknown>,
        )
        setState((s) => ({
          ...s,
          segments: s.segments.map((seg) =>
            seg.id === id ? { ...segment, weightedScore: seg.weightedScore } : seg,
          ),
        }))
        return segment
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  const deleteSegment = useCallback(
    async (id: string) => {
      // Optimistic remove
      const snapshot = state
      setState((s) => ({
        ...s,
        segments: s.segments.filter((seg) => seg.id !== id),
        coverage: s.coverage.filter((c) => c.segmentId !== id),
        canvas:
          s.canvas && s.canvas.beachheadSegmentId === id
            ? { ...s.canvas, beachheadSegmentId: null }
            : s.canvas,
      }))
      try {
        await marketSegmentationApi.deleteSegment(accountId, projectId, id)
      } catch (err) {
        setState(snapshot)
        throw err
      }
    },
    [accountId, projectId, state],
  )

  const reorderSegments = useCallback(
    async (orderedIds: string[]) => {
      setState((s) => {
        const byId = new Map(s.segments.map((seg) => [seg.id, seg]))
        const next = orderedIds
          .map((id, i) => {
            const seg = byId.get(id)
            return seg ? { ...seg, sortOrder: i } : null
          })
          .filter((x): x is MarketSegmentWithScore => x !== null)
        return { ...s, segments: next }
      })
      try {
        await marketSegmentationApi.reorderSegments(accountId, projectId, orderedIds)
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  // ==========================================================================
  // FIELD EDIT — source-flip applied optimistically via the shared util
  // ==========================================================================

  const updateField = useCallback(
    async (segmentId: string, path: 'sizing' | 'scoring', key: string, value: number | null) => {
      // Optimistic source-flip mirrors server `applyUserEdit*` exactly.
      setState((s) => ({
        ...s,
        segments: s.segments.map((seg) => {
          if (seg.id !== segmentId) return seg
          if (path === 'sizing') {
            if (!(SIZING_FIELD_KEYS as readonly string[]).includes(key)) return seg
            const k = key as SizingFieldKey
            const current = seg.sizing[k] ?? EMPTY_SIZING_FIELD
            return {
              ...seg,
              sizing: { ...seg.sizing, [k]: applyUserEditSizing(current, value) },
            }
          }
          if (!(SCORING_FIELD_KEYS as readonly string[]).includes(key)) return seg
          const k = key as ScoringFieldKey
          const current = seg.scoring[k] ?? EMPTY_SCORE_FIELD
          return {
            ...seg,
            scoring: { ...seg.scoring, [k]: applyUserEditScore(current, value) },
          }
        }),
      }))
      try {
        const { segment } = await marketSegmentationApi.updateField(
          accountId,
          projectId,
          segmentId,
          path,
          key,
          value,
        )
        // Reconcile with server's response (keeps weighted score in sync)
        setState((s) => ({
          ...s,
          segments: s.segments.map((seg) =>
            seg.id === segmentId
              ? { ...segment, weightedScore: seg.weightedScore }
              : seg,
          ),
        }))
        // Pull a fresh bundle to reconcile weighted score, which only the server computes.
        await load()
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  const resetField = useCallback(
    async (segmentId: string, path: 'sizing' | 'scoring', key: string) => {
      setState((s) => ({
        ...s,
        segments: s.segments.map((seg) => {
          if (seg.id !== segmentId) return seg
          if (path === 'sizing') {
            const k = key as SizingFieldKey
            const current = seg.sizing[k]
            if (!current || current.aiValue == null) return seg
            return { ...seg, sizing: { ...seg.sizing, [k]: resetSizingToAI(current) } }
          }
          const k = key as ScoringFieldKey
          const current = seg.scoring[k]
          if (!current || current.aiValue == null) return seg
          return { ...seg, scoring: { ...seg.scoring, [k]: resetScoreToAI(current) } }
        }),
      }))
      try {
        await marketSegmentationApi.resetField(accountId, projectId, segmentId, path, key)
        await load()
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  // ==========================================================================
  // PERSONA MUTATIONS
  // ==========================================================================

  const createPersona = useCallback(
    async (input: CreatePersonaInput) => {
      const { persona } = await marketSegmentationApi.createPersona(accountId, projectId, input)
      setState((s) => ({ ...s, personas: [...s.personas, persona] }))
      return persona as MarketPersona
    },
    [accountId, projectId],
  )

  const updatePersona = useCallback(
    async (id: string, updates: UpdatePersonaInput) => {
      setState((s) => ({
        ...s,
        personas: s.personas.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      }))
      try {
        const { persona } = await marketSegmentationApi.updatePersona(
          accountId,
          projectId,
          id,
          updates as Record<string, unknown>,
        )
        setState((s) => ({ ...s, personas: s.personas.map((p) => (p.id === id ? persona : p)) }))
        return persona
      } catch (err) {
        await load()
        throw err
      }
    },
    [accountId, projectId, load],
  )

  const deletePersona = useCallback(
    async (id: string) => {
      const snapshot = state
      setState((s) => ({
        ...s,
        personas: s.personas.filter((p) => p.id !== id),
        coverage: s.coverage.filter((c) => c.personaId !== id),
      }))
      try {
        await marketSegmentationApi.deletePersona(accountId, projectId, id)
      } catch (err) {
        setState(snapshot)
        throw err
      }
    },
    [accountId, projectId, state],
  )

  const linkPersona = useCallback(
    async (segmentId: string, personaId: string, coverage: CoverageLevel, notes?: string | null) => {
      const { link } = await marketSegmentationApi.linkPersona(
        accountId,
        projectId,
        segmentId,
        personaId,
        coverage,
        notes,
      )
      setState((s) => {
        const without = s.coverage.filter(
          (c) => !(c.segmentId === segmentId && c.personaId === personaId),
        )
        return { ...s, coverage: [...without, link] }
      })
      return link as SegmentPersonaLink
    },
    [accountId, projectId],
  )

  const unlinkPersona = useCallback(
    async (segmentId: string, personaId: string) => {
      const snapshot = state.coverage
      setState((s) => ({
        ...s,
        coverage: s.coverage.filter(
          (c) => !(c.segmentId === segmentId && c.personaId === personaId),
        ),
      }))
      try {
        await marketSegmentationApi.unlinkPersona(accountId, projectId, segmentId, personaId)
      } catch (err) {
        setState((s) => ({ ...s, coverage: snapshot }))
        throw err
      }
    },
    [accountId, projectId, state.coverage],
  )

  const generateScoring = useCallback(
    async (segmentId: string) => {
      const { segment } = await marketSegmentationApi.generateScoring(
        accountId,
        projectId,
        segmentId,
      )
      setState((s) => ({
        ...s,
        segments: s.segments.map((seg) =>
          seg.id === segmentId ? { ...segment, weightedScore: seg.weightedScore } : seg,
        ),
      }))
      // Reload to get the server-computed weightedScore for the new scoring.
      await load()
      return segment as MarketSegmentWithScore
    },
    [accountId, projectId, load],
  )

  const generateSizing = useCallback(
    async (segmentId: string) => {
      const { segment } = await marketSegmentationApi.generateSizing(
        accountId,
        projectId,
        segmentId,
      )
      setState((s) => ({
        ...s,
        segments: s.segments.map((seg) =>
          seg.id === segmentId ? { ...segment, weightedScore: seg.weightedScore } : seg,
        ),
      }))
      return segment as MarketSegmentWithScore
    },
    [accountId, projectId],
  )

  const generateBrief = useCallback(
    async (segmentId: string) => {
      const { brief } = await marketSegmentationApi.generateBrief(
        accountId,
        projectId,
        segmentId,
      )
      return brief
    },
    [accountId, projectId],
  )

  const listBriefs = useCallback(
    async (segmentId: string) => {
      const { briefs } = await marketSegmentationApi.listBriefs(
        accountId,
        projectId,
        segmentId,
      )
      return briefs
    },
    [accountId, projectId],
  )

  return {
    ...state,
    reload: load,
    updateWeights,
    setBeachhead,
    createSegment,
    updateSegment,
    deleteSegment,
    reorderSegments,
    updateField,
    resetField,
    createPersona,
    updatePersona,
    deletePersona,
    linkPersona,
    unlinkPersona,
    generateScoring,
    generateSizing,
    generateBrief,
    listBriefs,
  }
}
